import {
  ALL_POOLS,
  getCartColor,
  getSchemeId, getTechnicalSchemeId
} from "../../../services/live-interface/dior/dior-interface-service";
import {
  checkProcessDivergences,
  getNextTheoreticalStep,
  getTheoreticalMatchFromCartHistory
} from "../../../services/live-interface/dior/dior-check-errors";
import { formatSecondsToHoursMinutesSeconds, formatSecondsToMinutesSeconds } from "../../../helpers/date/date-helper";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";


export const DiorKanbanCard = ({ cart, showTimes=true, showErrors=true, processes=[] }) => {
  const cartHistorySchemeId = cart.history.map((step) => ({...step, pool_id: getSchemeId(step.pool_id)}));

  const lastStep = (() => {
    if (!cart?.history || cart.history.length === 0) return null;
    return cart.history[cart.history.length - 1];
  })();

  let process = processes.find((process) => process.name === cart.rack_type)?.pattern_data?.[0]?.json;
  if (!process) process = [];

  const { theoreticalStep } = getTheoreticalMatchFromCartHistory(cartHistorySchemeId, process);
  const { nextTheoreticalStep } = getNextTheoreticalStep(cartHistorySchemeId, process);
  const theoreticalDuration = theoreticalStep?.duration_min ?? 0;

  const cartPoolRealId = lastStep?.pool_id ?? null;
  const cartValue = lastStep?.value ?? null;
  const cartStepInProgress = !lastStep?.done ?? false;
  const minDurationOver = cartValue > theoreticalDuration;

  const cartPoolSchemeId = getSchemeId(cartPoolRealId);
  const cartPoolTechnicalSchemeId = getTechnicalSchemeId(cartPoolSchemeId);

  const percent = theoreticalDuration > 0 ? Math.min(lastStep?.value / theoreticalDuration * 100, 100) : 100;

  const { has_error: cartHasError, steps_error: cartErrors } = checkProcessDivergences(process, cartHistorySchemeId);
  const isCurrentPoolError = cartErrors.find((error) => error.details.pool_id === cartPoolSchemeId) !== undefined;

  const borderColor = isCurrentPoolError ? '#F9224B' : minDurationOver ? '#17E15B' : 'transparent';
  const shouldBlink = isCurrentPoolError || minDurationOver;

  const hasBathErrors = cartErrors.find((error) => error.reason === 'missing_step' || error.reason === 'unexpected_step') !== undefined;
  const lastStepHasDurationError = cartValue > (theoreticalStep?.duration_max ?? 0);

  return (
    <div className={`w-full bg-perception-black-700 rounded-md ${showTimes ? 'p-4' : 'p-2'} border-3 ${shouldBlink ? 'blink-border' : ''}`}
         style={{ '--blink-border-color': borderColor, borderColor: borderColor }}>
      {/* Header */}
      <div className="flex w-full max-w-full">
        <div className="flex items-center flex-1 whitespace-nowrap overflow-hidden">
          {/*<div className="size-3 min-w-3 mr-2 rounded-full" style={{ backgroundColor: getCartColor() }} />*/}
          <div className="px-1 text-xs bg-perception-black-800 px-2 py-1 mr-2 rounded-md">
            {cart.rack_id > 10000 ? '(No ID)' : `#${cart.rack_id}`}
          </div>
          <span className="font-semibold">{ALL_POOLS[cartPoolTechnicalSchemeId].name}</span>
        </div>

        {/*<div className="w-auto">*/}
        {/*  {*/}
        {/*    processIsFinished && cartHasError ? <div className="bg-error text-white text-xs px-2 py-1 rounded-md">Process finished with erros</div>*/}
        {/*      : cartHasError ? <div className="bg-error text-white text-xs px-2 py-1 rounded-md">Error</div>*/}
        {/*        : !minDurationOver ? <div className="bg-perception-blue text-white text-xs px-2 py-1 rounded-md">In progress</div>*/}
        {/*          : processIsFinished ? <div className="bg-perception-success-300 text-white text-xs px-2 py-1 rounded-md">Process finished</div>*/}
        {/*            : <div className="bg-perception-success-300 text-white text-xs px-2 py-1 rounded-md">Step finished</div>*/}
        {/*  }*/}
        {/*</div>*/}
      </div>

      {/* Progress bar */}
      <div className="flex flex-col my-1">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 rounded-full bg-perception-black-500 overflow-hidden">
            <div className={`h-full ${lastStepHasDurationError ? 'bg-error' : minDurationOver ? 'bg-perception-success-300' : 'bg-perception-blue'} rounded-full`} style={{ width: percent + '%'}} />
          </div>
          <span className="text-sm text-gray-300">{Math.round(percent)}%</span>
        </div>
      </div>

      {/* Times */}
      {
        showTimes &&
        <>
          {
            lastStep &&
            <div className="flex justify-between mb-2 text-sm text-gray-400">
              <div>
                <span className="mr-2">Start:</span>
                <span className="font-semibold text-white">{formatSecondsToHoursMinutesSeconds(lastStep?.start_ts)}</span>
              </div>
              <div>
                <span className="mr-2">{cartStepInProgress ? 'Estimated end' : 'End'}:</span>
                <span className="font-semibold text-white">
                    {
                      cartStepInProgress ?
                        formatSecondsToHoursMinutesSeconds(lastStep?.start_ts + theoreticalDuration) :
                        formatSecondsToHoursMinutesSeconds(lastStep?.start_ts + lastStep?.value)
                    }
                  </span>
              </div>

              {/* Remaining time */}
              <div className="flex text-sm text-gray-400">
                <span className="mr-2">Time remaining:</span>
                <span className={`${lastStepHasDurationError ? 'text-error' : minDurationOver ? 'text-perception-success-300' : 'text-perception-blue'} font-semibold`}>
                  {formatSecondsToMinutesSeconds(Math.max(theoreticalDuration - lastStep?.value, 0))}
                </span>
              </div>
            </div>
          }
        </>
      }

      <div className="flex justify-start items-center">
        <div className={`text-xs ${hasBathErrors ? 'bg-error text-white' : 'bg-perception-black-800'} px-2 py-1 rounded-md flex items-center gap-1`}>
          {hasBathErrors ? <XCircleIcon className="size-3"/> : <CheckCircleIcon className="size-3"/>}
          Bath #{cartPoolSchemeId}
        </div>
      </div>

      {/* Errors */}
      {
        // Not used anymore
        showErrors &&
          <div className="bg-perception-black-500 rounded-md mt-2 p-2 text-sm">
            {
              cartHasError ?
                <div className="flex flex-col gap-2">
                  {cartErrors.length} error(s) reported
                  <div className="flex flex-col gap-2">
                    {
                      cartErrors.slice(-1).map((error, errorIndex) => {
                        let errorTypeText = 'Unknown error';
                        if (error.reason === 'missing_step') {
                          const expectedPool = ALL_POOLS[error.details.expected_pool_id];
                          errorTypeText = 'Missing step: ' + expectedPool.name;
                        }
                        else if (error.reason === 'unexpected_step') {
                          const unexpectedPool = ALL_POOLS[error.details.pool_id];
                          errorTypeText = 'Unexpected step: ' + unexpectedPool.name;
                        }
                        else if (error.reason === 'duration_too_long') {
                          const pool = ALL_POOLS[error.details.pool_id];
                          errorTypeText = 'Too long in ' + pool.name + ' (' + formatSecondsToMinutesSeconds(error.details.value) + ')';
                        }
                        else if (error.reason === 'duration_too_short') {
                          const pool = ALL_POOLS[error.details.pool_id];
                          errorTypeText = 'Too quick in ' + pool.name + ' (' + formatSecondsToMinutesSeconds(error.details.value) + ')';
                        }

                        return (
                          <div key={errorIndex} className="flex items-center gap-2">
                            <div className="rounded-full text-xs px-2 text-nowrap text-center bg-error text-white w-auto">
                              Bath #{error.id}
                            </div>
                            {errorTypeText}
                          </div>
                        )}
                      )
                    }
                  </div>
                </div>
                :
                <div className="text-center">No error detected</div>
            }
          </div>
      }
      {
        nextTheoreticalStep &&
        <div className="bg-perception-black-500 rounded-md mt-2 p-2 text-sm text-center">
          Next step: <span className="font-bold text-perception-blue">
              {ALL_POOLS[nextTheoreticalStep.pool_id].name}
            </span>
        </div>
      }
    </div>
  );
};